import axios from "axios";
import useLogin from "hooks/useLogin";
import { URL_TRD, URL_TRD_V2 } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import { isNullOrUndefined } from "utils/validations";

export default function useTrd() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Retrieves the tree structure by domain.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getTreeByDomain = async () => {
    const response = await axios.get(`${URL_TRD}/trd/tree/`, {
      headers,
      endpointError: endpointError.trd.getTreeByDomain,
    });
    return response;
  };

  /**
   * Retrieves a tree by domain that is cloned for role permissions.
   * @param {string} action - The action to perform for retrieving the tree.
   * @returns {Promise} A promise that resolves with the response containing the tree data.
   */
  const getTreeByDomainRoles = async (action) => {
    const response = await axios.get(`${URL_TRD}/trd/tree/role-permissions/?action=${action}`, {
      headers,
      endpointError: endpointError.trd.getTreeByDomainCloned,
    });
    return response;
  };

  /**
   * Retrieves the tree structure for a specific expedient within a background by their IDs.
   * @param {string} backgroundId - The ID of the background.
   * @param {string} expedientId - The ID of the expedient.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getTreeExpedientByDomain = async (backgroundId, expedientId) => {
    const response = await axios.get(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/expedients/${expedientId}/tree/`,
      {
        headers,
        endpointError: endpointError.trd.getTreeExpedientByDomain,
      },
    );
    return response;
  };

  /**
   * Retrieves the tree expedient by domain roles.
   * @param {string} backgroundId - The ID of the background.
   * @param {string} expedientId - The ID of the expedient.
   * @param {string} action - The action to perform.
   * @returns {Promise} The response containing the tree expedient with role permissions.
   */
  const getTreeExpedientByDomainRoles = async (backgroundId, expedientId, action) => {
    const response = await axios.get(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/expedients/${expedientId}/tree/role-permissions/?action=${action}`,
      {
        headers,
        endpointError: endpointError.trd.getTreeExpedientByDomain,
      },
    );
    return response;
  };

  /**
   * Retrieves expedients based on the provided parameters.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} parentId - The ID of the parent series or subseries.
   * @param {string} backgroundId - The ID of the background.
   * @param {string} search - (Optional) Search query for filtering expedients by name, subject, or code.
   * @param {string} status - (Optional) Status for filtering expedients.
   * @returns {Promise<Object>} A Promise that resolves with the data of the retrieved expedients.
   */
  const getExpedients = async (page, per_page, parentId, backgroundId, search, status) => {
    if (search) {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}&count_files=true&code=${search}
        `,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    } else if (isNullOrUndefined(status) === false) {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/?page=${page}&per_page=${per_page}&status=${status}`,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    } else {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/?page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    }
  };

  /**
   * Retrieves expedient roles based on specified parameters.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} parentId - The parent ID for the expedient.
   * @param {string} backgroundId - The background ID for the expedient.
   * @param {string} search - The search query for filtering expedients.
   * @param {string} action - The action to be performed.
   * @param {string} status - The status of the expedient.
   * @returns {Promise} The data containing expedient roles.
   */
  const getExpedientsRoles = async (
    page,
    per_page,
    parentId,
    backgroundId,
    search,
    action,
    status,
  ) => {
    if (search) {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/role-permissions/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}&count_files=true&code=${search}&action=${action}
        `,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    } else if (isNullOrUndefined(status) === false) {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/role-permissions/?page=${page}&per_page=${per_page}&status=${status}&action=${action}`,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    } else {
      const response = await axios.get(
        `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/role-permissions/?page=${page}&per_page=${per_page}&action=${action}`,
        {
          headers,
          endpointError: endpointError.trd.getExpedients,
        },
      );
      return response.data;
    }
  };

  /**
   * Fetches an expedient's data by its UUID from the specified endpoint.
   * @param {string} expedientId - The UUID of the expedient to retrieve.
   * @returns {Promise<Object>} - A promise that resolves to the data of the expedient.
   * @throws {Error} - Throws an error if the request fails.
   */
  const getExpedientByUUID = async (expedientId) => {
    const response = await axios.get(`${URL_TRD}/expedients/${expedientId}/`, {
      headers,
      endpointError: endpointError.trd.getExpedientByUUID,
    });
    return response.data;
  };

  /*Get the list of expedients created in the TRD */
  const getExpedientsByName = async (page, per_page, parentId, backgroundId, search) => {
    const response = await axios.get(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}&count_files=true`,
      {
        headers,
        endpointError: endpointError.trd.getExpedientsByName,
      },
    );
    return response.data;
  };

  /**
   * Retrieves expedients by name and roles from the TRD API.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} parentId - The parent ID for the expedients.
   * @param {string} backgroundId - The background ID for the expedients.
   * @param {string} search - The search query for filtering expedients by name or subject.
   * @param {string} action - The action to perform on the expedients.
   * @returns {Promise} A promise that resolves with the data of the retrieved expedients.
   */
  const getExpedientsByNameRoles = async (
    page,
    per_page,
    parentId,
    backgroundId,
    search,
    action,
  ) => {
    const response = await axios.get(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${parentId}/expedients/role-permissions/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}&count_files=true&action=${action}`,
      {
        headers,
        endpointError: endpointError.trd.getExpedientsByNameRoles,
      },
    );
    return response.data;
  };

  /**
   * Retrieves files by expedient ID with pagination and search filters.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} expedientId - The ID of the expedient.
   * @param {string} search - The search term for file names and subjects.
   * @returns {Promise<Object>} The response data from the server.
   * @throws Will throw an error if the request fails.
   */
  const getFilesByExpedient = async (page, per_page, expedientId, search) => {
    const response = await axios.get(
      `${URL_TRD_V2}/file/trd/expedient/${expedientId}/files/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}`,
      {
        headers,
        endpointError: endpointError.trd.getFilesByExpedient,
      },
    );
    return response.data;
  };

  /**
   * Retrieves files based on roles associated with a specific expedient.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of files per page.
   * @param {string} expedientId - The ID of the expedient to retrieve files for.
   * @param {string} search - The search query for file names or subjects.
   * @param {string} action - The action associated with the files.
   * @returns {Promise} A promise that resolves with the data of the retrieved files.
   */
  const getFilesByExpedientRoles = async (page, per_page, expedientId, search, action) => {
    const response = await axios.get(
      `${URL_TRD_V2}/file/trd/expedient/${expedientId}/files/role-permissions/?page=${page}&per_page=${per_page}&name=${search}&subject=${search}&action=${action}`,
      {
        headers,
        endpointError: endpointError.trd.getFilesByExpedientRoles,
      },
    );
    return response.data;
  };

  /**
   * Creates a new fund with the given element name.
   * @param {string} element - The name of the element to create as a fund.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const createFund = async (element) => {
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/`,
      { name: element },
      {
        headers,
        endpointError: endpointError.trd.createFund,
      },
    );
    return response;
  };

  /**
   * Creates a new section for a given background.
   * @param {Object} section - The section data to be created.
   * @param {string} backgroundId - The ID of the background to which the section belongs.
   * @returns {Promise<Object>} The response from the server.
   * @throws {Error} If the request fails.
   */
  const createSection = async (section, backgroundId) => {
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/sections/`,
      section,
      {
        headers,
        endpointError: endpointError.trd.createSection,
      },
    );
    return response;
  };

  /**
   * Creates a subsection under a specific section in a background.
   * @param {Object} data - The data for the new subsection.
   * @param {Object} foldersId - An object containing IDs for the background and section.
   * @param {string} foldersId.backgroundId - The ID of the background where the subsection will be created.
   * @param {string} foldersId.sectionId - The ID of the section under which the subsection will be created.
   * @returns {Promise<Object>} The response from the Axios post request.
   * @throws {Error} Throws an error if the request fails.
   */
  const createSubSectionBySection = async (data, foldersId) => {
    const { backgroundId, sectionId } = foldersId;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/sections/${sectionId}/subsections/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createSubSectionBySection,
      },
    );
    return response;
  };

  /*Create a new Series */
  const createSerieBySectionOrSubSection = async (data, foldersId, parentData) => {
    const { backgroundId } = foldersId;
    const { id } = parentData;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/sections-subsections/${id}/series/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createSerieBySectionOrSubSection,
      },
    );
    return response;
  };

  /**
   * Creates a subseries under a specified series in a background.
   * @param {Object} data - The data for the subseries being created.
   * @param {Object} foldersId - An object containing folder identifiers.
   * @param {string} foldersId.backgroundId - The ID of the background folder.
   * @param {Object} parentData - The data of the parent series.
   * @param {string} parentData.id - The ID of the parent series to which the subseries belongs.
   * @returns {Promise<Object>} A promise that resolves to the response from the API.
   * @throws {Error} Throws an error if the API request fails.
   */
  const createSubserieBySerie = async (data, foldersId, parentData) => {
    const { backgroundId } = foldersId;
    const { id } = parentData;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series/${id}/subseries/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createSubserieBySerie,
      },
    );
    return response;
  };

  /**
   * Creates a new expedient by sending a POST request to the specified API endpoint.
   * @param {Object} data - The data for the new expedient. This should contain all required attributes needed for the creation.
   * @param {Object} foldersId - An object containing IDs necessary for the API request.
   * @param {string} foldersId.backgroundId - The ID of the background under which the expedient is created.
   * @param {string} expedientParent - The ID of the parent expedient under which this new expedient will be nested.
   * @returns {Promise<Object>} The response from the API, containing the created expedient's details.
   * @throws {Error} Will throw an error if the request fails.
   */
  const createExpedient = async (data, foldersId, expedientParent) => {
    const { backgroundId } = foldersId;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${expedientParent}/expedients/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createExpedient,
      },
    );
    return response;
  };

  /**
   * Create a new subdivision for a specific background and expedient.
   * @param {Object} data - The data for the subdivision to be created.
   * @param {Object} foldersId - An object containing the IDs for background and expedient.
   * @param {string} foldersId.backgroundId - The ID of the background.
   * @param {string} foldersId.expedientId - The ID of the expedient.
   * @returns {Promise<Object>} The response from the API after creating the subdivision.
   * @throws {Error} Throws an error if the API request fails.
   */
  const createSubDivision = async (data, foldersId) => {
    const { backgroundId, expedientId } = foldersId;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/expedients/${expedientId}/subdivisions/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createSubDivision,
      },
    );
    return response;
  };

  /**
   * Create a new subdivision for a specific background and expedient.
   * @param {Object} data - The data for the subdivision to be created.
   * @param {Object} foldersId - An object containing the IDs for background and expedient.
   * @param {string} foldersId.backgroundId - The ID of the background.
   * @param {string} foldersId.expedientId - The ID of the expedient.
   * @returns {Promise<Object>} The response from the API after creating the subdivision.
   * @throws {Error} Throws an error if the API request fails.
   */
  const createDirectory = async (data, foldersId) => {
    const { backgroundId, subDivisionId } = foldersId;
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/subdivisions/${subDivisionId}/directories/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createDirectory,
      },
    );
    return response;
  };

  /**
   * Asynchronously checks if a tree exists at the specified endpoint.
   * @async
   * @function getTreeExist
   * @returns {Promise<Object>} A promise that resolves to the response data from the API.
   * @throws {Error} Will throw an error if the request to the API fails.
   */
  const getTreeExist = async () => {
    const response = await axios.get(`${URL_TRD}/trd/tree/exists/`, {
      headers,
      endpointError: endpointError.trd.getTreeExist,
    });
    return response.data;
  };

  /**
   * Updates an expedient with the given ID and data.
   * @param {string} id - The ID of the expedient to update.
   * @param {Object} exp - The object containing the updated expedient data.
   * @param {string} [exp.fieldName] - Description of a field that can be updated.
   * @returns {Promise<Object>} The response from the server after updating the expedient.
   * @throws {Error} Throws an error if the request fails or the expedient cannot be updated.
   */
  const updateExpedient = async (id, exp) => {
    const response = await axios.patch(`${URL_TRD}/trd/expedients/${id}/`, exp, {
      headers,
      endpointError: endpointError.trd.updateExpedient,
    });
    return response;
  };

  /*Update an existing Documental Unit */
  /*This update service is not used has been deprecated*/
  const updateSubDivision = async (editSubDivisionForm, backgroundId, subDivisionId) => {
    const response = await axios.patch(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/subdivisions/${subDivisionId}/`,
      editSubDivisionForm,
      {
        headers,
        endpointError: endpointError.trd.updateSubDivision,
      },
    );
    return response;
  };

  /**
   * Updates a directory with the given data.
   * @param {Object} editDirectoryForm - The object containing the data to update the directory.
   * @param {string} backgroundId - The ID of the background associated with the directory.
   * @param {string} subDivisionId - The ID of the subdivision that contains the directory.
   * @param {string} subDirectoryId - The ID of the directory to be updated.
   * @returns {Promise<Object>} The response from the server after attempting to update the directory.
   * @throws {Error} Throws an error if the update fails.
   */
  const updateDirectory = async (
    editDirectoryForm,
    backgroundId,
    subDivisionId,
    subDirectoryId,
  ) => {
    const response = await axios.patch(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/subdivisions/${subDivisionId}/directories/${subDirectoryId}/`,
      editDirectoryForm,
      {
        headers,
        endpointError: endpointError.trd.updateDirectory,
      },
    );
    return response;
  };

  /**
   * Updates a specific section with the provided data.
   * This function sends a PATCH request to the server to update a section identified by its UUID.
   * @param {Object} data - The data to update the section with. This should be an object
   * @param {string} section_uuid - The unique identifier of the section to be updated.
   * @returns {Promise<Object>} The response from the server after updating the section.
   * @throws {Error} Throws an error if the request fails or the server returns an error response.
   */
  const updateSection = async (data, section_uuid) => {
    const response = await axios.patch(`${URL_TRD}/trd/sections/${section_uuid}/`, data, {
      headers,
      endpointError: endpointError.trd.updateSection,
    });
    return response;
  };

  /**
   * Updates a series with the specified UUID using a PATCH request.
   * @param {string} uuidSerie - The unique identifier of the series to be updated.
   * @param {Object} data - The data to update the series with.
   * @param {Object} data.propertyName - Description of the properties that are being updated (replace 'propertyName'
   * @returns {Promise<Object>} The response from the server containing the updated series data.
   * @throws {Error} Throws an error if the request fails.
   */
  const updateSerie = async (uuidSerie, data) => {
    const response = await axios.patch(`${URL_TRD}/trd/series/${uuidSerie}/`, data, {
      headers,
      endpointError: endpointError.trd.updateSerie,
    });
    return response;
  };

  /**
   * Creates a new documentary type by sending a POST request to the specified endpoint.
   * @param {Object} data - The data for the documentary type to be created.
   * @param {string} data.name - The name of the documentary type.
   * @param {string} data.description - A description of the documentary type.
   * @param {string} data.category - The category associated with the documentary type.
   * @returns {Promise<Object>} The response from the API call, containing details of the created documentary type.
   * @throws {Error} Throws an error if the API call fails.
   */
  const createDocumentaryType = async (data) => {
    const response = await axios.post(`${URL_TRD}/trd/documentary-type/`, data, {
      headers,
      endpointError: endpointError.trd.createDocumentaryType,
    });
    return response;
  };

  /**
   * Updates a documentary type with the specified data.
   * @param {string} documentaryId - The ID of the documentary type to update.
   * @param {Object} data - The data to update the documentary type with.
   * @returns {Promise<Object>} The response from the server after the update.
   * @throws {Error} Will throw an error if the update fails.
   */
  const updateDocumentaryType = async (documentaryId, data) => {
    const response = await axios.put(`${URL_TRD}/trd/documentary-type/${documentaryId}/`, data, {
      headers,
      endpointError: endpointError.trd.updateDocumentaryType,
    });
    return response;
  };

  /**
   * Updates a sub-series identified by its UUID.
   * This function sends a PATCH request to update the specified sub-series
   * with the provided data. The request is sent to the API endpoint
   * configured with the base URL, and it expects an authorization header
   * to be present.
   * @param {string} uuidSubSerie - The UUID of the sub-series to be updated.
   * @param {Object} data - The data to be updated for the sub-series. This
   * can include various fields as defined by the API.
   * @returns {Promise<Object>} - A promise that resolves to the response
   */
  const updateSubSerie = async (uuidSubSerie, data) => {
    const response = await axios.patch(`${URL_TRD}/trd/sub-series/${uuidSubSerie}/`, data, {
      headers,
      endpointError: endpointError.trd.updateSubSerie,
    });
    return response;
  };

  /**
   * Updates a subsection by making a PATCH request to the specified endpoint.
   * @param {Object} data - The data to update the subsection with. This should include the fields to be modified based on the server's expectations.
   * @param {string} subSection_uuid - The UUID of the subsection to update.
   * @returns {Promise<Object>} A promise that resolves to the response object from the PATCH request.
   * @throws {Error} Throws an error if the request fails or if an error is returned from the endpoint.
   */
  const updateSubSection = async (data, subSection_uuid) => {
    const response = await axios.patch(`${URL_TRD}/trd/subsections/${subSection_uuid}/`, data, {
      headers,
      endpointError: endpointError.trd.updateSubSection,
    });
    return response;
  };

  /**
   * Updates the background with the specified UUID.
   * @param {Object} data - The data to update the background with.
   * @param {string} background_uuid - The UUID of the background to update.
   * @returns {Promise<Object>} A promise that resolves to the response object from the update request.
   * @throws {Error} Throws an error if the request fails.
   */
  const updateBackground = async (data, background_uuid) => {
    const response = await axios.patch(`${URL_TRD}/trd/backgrounds/${background_uuid}/`, data, {
      headers,
      endpointError: endpointError.trd.updateBackground,
    });
    return response;
  };

  /**
   * Fetches the structure of a specific series or subseries based on the provided background and series/subseries IDs.
   * @param {string} backgroundId - The ID of the background to which the series or subseries belongs.
   * @param {string} serieSubserieId - The ID of the series or subseries to fetch the structure for.
   * @returns {Promise<Object>} A promise that resolves to the response containing the structure of the series or subseries.
   * @throws {Error} Throws an error if the request fails or if the response is not as expected.
   */
  const getSerieSubserieStructure = async (backgroundId, serieSubserieId) => {
    const response = await axios.get(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${serieSubserieId}/structure/`,
      {
        headers,
        endpointError: endpointError.trd.getSerieSubserieStructure,
      },
    );
    return response;
  };

  /*Create a new documental unit to the structure in a series or subseries */
  const createSubdivisionByStructure = async (data, backgroundId, serieSubserieId) => {
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${serieSubserieId}/structure/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createSubdivisionByStructure,
      },
    );
    return response;
  };

  /*Create a new directory to the structure in a series or subseries */
  const createDirectoryByStructure = async (data, backgroundId, subdivisionId) => {
    const response = await axios.post(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/base-subdivisions/${subdivisionId}/structure/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createDirectoryByStructure,
      },
    );
    return response;
  };

  /*Update an existing Subdivision in the structure in a series or subseries */
  const updateSubdivisionStructure = async (data, backgroundId, serieSubserieId) => {
    const response = await axios.patch(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${serieSubserieId}/structure/base-subdivisions/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.createDirectoryByStructure,
      },
    );
    return response;
  };

  /*Update an existing directory in the structure in a series or subseries */
  const updateDirectoryStructure = async (data, backgroundId, serieSubserieId) => {
    const response = await axios.patch(
      `${URL_TRD}/trd/backgrounds/${backgroundId}/series-subseries/${serieSubserieId}/structure/base-directories/`,
      data,
      {
        headers,
        endpointError: endpointError.trd.updateDirectoryStructure,
      },
    );
    return response;
  };

  /**
   * Fetches a list of documentary types from the TRD API.
   * This function makes an asynchronous request to retrieve documentary types,
   * optionally filtered by a search name and status. The result includes pagination
   * parameters such as the current page number and the number of items per page.
   * @param {number} page - The current page number to retrieve.
   * @param {number} per_page - The number of items per page to retrieve.
   * @param {string} searchName - The name to filter documentary types by (can be empty).
   * @param {string} status - The status to filter documentary types by (can be an empty string).
   */
  const getDocumentalTypes = async (page, per_page, searchName, status) => {
    let responseDocumentalTypes;
    if (isNullOrUndefined(status) === false) {
      responseDocumentalTypes = await axios.get(
        `${URL_TRD}/trd/documentary-type/?page=${page}&per_page=${per_page}&name=${searchName}&status=${status}`,
        {
          headers,
          endpointError: endpointError.trd.getDocumentalTypes,
        },
      );
    } else {
      responseDocumentalTypes = await axios.get(
        `${URL_TRD}/trd/documentary-type/?page=${page}&per_page=${per_page}&name=${searchName}`,
        {
          headers,
          endpointError: endpointError.trd.getDocumentalTypes,
        },
      );
    }
    return responseDocumentalTypes;
  };

  /**
   * Deletes a level from the TRD tree.
   * @param {string} uuidLevel - The unique identifier of the level to be deleted.
   * @param {string} enumLevel - The enumeration representing the level's type or category.
   * @returns {Promise<Object>} The response from the delete request.
   * @throws {Error} Throws an error if the delete request fails.
   */
  const deleteLevelTrd = async (uuidLevel, enumLevel) => {
    const responseDeleteLevel = await axios.delete(
      `${URL_TRD}/trd/tree/${uuidLevel}/level/${enumLevel}/`,
      {
        headers,
        endpointError: endpointError.trd.deleteLevelTrd,
      },
    );
    return responseDeleteLevel;
  };

  /*Delete an existing level in the structure*/
  const deleteLevelOrdinationTrd = async (serieSubserieId, uuidLevel, enumLevel) => {
    const responseDeleteLevelOrdination = await axios.delete(
      `${URL_TRD}/trd/series-subseries/${serieSubserieId}/level/${uuidLevel}/${enumLevel}/`,
      {
        headers,
        endpointError: endpointError.trd.deleteLevelOrdinationTrd,
      },
    );
    return responseDeleteLevelOrdination;
  };

  /**
   * Retrieves the path set for an expedient record identified by its UUID.
   * @param {string} recordUuid - The UUID of the expedient record for which to retrieve the path set.
   * @returns {Promise<any>} A promise that resolves to the data representing the path set of the expedient record.
   * @throws {Error} If an error occurs during the HTTP request or data retrieval.
   */
  const getPathSetExpedient = async (recordUuid) => {
    const response = await axios.get(`${URL_TRD}/trd/expedients/record/${recordUuid}/path/`, {
      headers,
      endpointError: endpointError.trd.getPathSetExpedient,
    });
    return response.data;
  };

  /**
   * Sets the path information data for an expedient identified by its UUID.
   * @param {object} expedientPathSet - The path information data to be set for the expedient.
   * @param {string} expedientUuid - The UUID of the expedient for which the path information data is being set.
   * @returns {Promise<object>} A promise that resolves to the response object from the server.
   * The response object typically contains information about the success or failure of the request.
   * @throws {Error} If there's an error during the HTTP request or if the response indicates an error.
   */
  const setExpedientPath = async (expedientPathSet, expedientUuid) => {
    const response = await axios.post(
      `${URL_TRD}/trd/expedients/${expedientUuid}/related-record/`,
      expedientPathSet,
      {
        headers,
        endpointError: endpointError.trd.setExpedientPath,
      },
    );
    return response;
  };

  /**
   * Deletes the path set for a specific expedient record identified by its UUID.
   * @param {string} recordUuid - The UUID of the expedient record whose path set is to be deleted.
   * @returns {Promise<object>} A promise that resolves to the response object from the server indicating the success or failure of the deletion operation.
   */
  const deletePathSetExpedient = async (recordUuid, formUuid) => {
    const response = await axios.delete(
      `${URL_TRD}/trd/expedients/record/${recordUuid}/path/?form_uuid=${formUuid}`,
      {
        headers,
        endpointError: endpointError.trd.deletePathSetExpedient,
      },
    );
    return response;
  };

  /**
   * Fetches documentary types from a specific series.
   * @param {string} levelUuid - Unique identifier of the series level.
   * @param {number} page - Current page number for pagination.
   * @param {number} per_page - Number of items per page.
   * @param {string} searchName - Name filter used to search documentary types.
   * @returns {Promise<Object>} Resolves with the response data from the API.
   */
  const getDocumentaryTypesFromSerie = async (
    levelUuid,
    page,
    per_page,
    searchName,
    includeStandards,
  ) => {
    let url = `${URL_TRD}/trd/series/${levelUuid}/documentary-types/?page=${page}&per_page=${per_page}&name=${searchName}`;

    if (isNullOrUndefined(includeStandards) === false) {
      url += `&include_standards=${includeStandards}`;
    }

    const response = await axios.get(url, {
      headers,
      endpointError: endpointError.trd.getDocumentaryTypesFromSerie,
    });

    return response.data;
  };

  /**
   * Fetches documentary types associated with a specific sub-series.
   * @param {string} levelUuid - Unique identifier of the sub-series level.
   * @param {number} page - Current page number for pagination.
   * @param {number} per_page - Number of items per page.
   * @param {string} searchName - Name filter used to search documentary types.
   * @returns {Promise<Object>} Resolves with the response data from the API.
   */
  const getDocumentaryTypesFromSubserie = async (
    levelUuid,
    page,
    per_page,
    searchName,
    includeStandards,
  ) => {
    let url = `${URL_TRD}/trd/sub-series/${levelUuid}/documentary-types/?page=${page}&per_page=${per_page}&name=${searchName}`;

    if (isNullOrUndefined(includeStandards) === false) {
      url += `&include_standards=${includeStandards}`;
    }

    const response = await axios.get(url, {
      headers,
      endpointError: endpointError.trd.getDocumentaryTypesFromSubserie,
    });

    return response.data;
  };

  /**
   * Deletes a documentary type from a series.
   * @param {string} levelUuid - The UUID of the series (level).
   * @param {string} uuid - The UUID of the documentary type to delete.
   * @returns {Promise<Object>} The Axios response object.
   */
  const deleteDocumentaryTypeFromSerie = async (levelUuid, uuid) => {
    const response = await axios.delete(
      `${URL_TRD}/trd/series/${levelUuid}/documentary-types/${uuid}/`,
      {
        headers,
        endpointError: endpointError.trd.deleteDocumentaryTypeFromSerie,
      },
    );
    return response;
  };

  /**
   * Deletes a documentary type from a subseries.
   * @param {string} levelUuid - The UUID of the subseries level.
   * @param {string} uuid - The UUID of the documentary type to be deleted.
   * @returns {Promise<Object>} The Axios response object.
   */
  const deleteDocumentaryTypeFromSubserie = async (levelUuid, uuid) => {
    const response = await axios.delete(
      `${URL_TRD}/trd/sub-series/${levelUuid}/documentary-types/${uuid}/`,
      {
        headers,
        endpointError: endpointError.trd.deleteDocumentaryTypeFromSubserie,
      },
    );
    return response;
  };

  return {
    getTreeByDomain,
    getTreeByDomainRoles,
    createFund,
    createSection,
    createSerieBySectionOrSubSection,
    createSubSectionBySection,
    createSubserieBySerie,
    createExpedient,
    createSubDivision,
    getExpedients,
    getExpedientsRoles,
    getExpedientsByName,
    getExpedientsByNameRoles,
    getFilesByExpedient,
    getFilesByExpedientRoles,
    createDirectory,
    getTreeExpedientByDomain,
    getTreeExpedientByDomainRoles,
    getTreeExist,
    updateExpedient,
    updateSubDivision,
    updateDirectory,
    updateSection,
    updateSubSerie,
    updateSubSection,
    updateBackground,
    updateSerie,
    updateDocumentaryType,
    createDocumentaryType,
    getSerieSubserieStructure,
    createSubdivisionByStructure,
    createDirectoryByStructure,
    updateSubdivisionStructure,
    updateDirectoryStructure,
    getDocumentalTypes,
    deleteLevelTrd,
    deleteLevelOrdinationTrd,
    getPathSetExpedient,
    setExpedientPath,
    deletePathSetExpedient,
    getExpedientByUUID,
    getDocumentaryTypesFromSerie,
    getDocumentaryTypesFromSubserie,
    deleteDocumentaryTypeFromSerie,
    deleteDocumentaryTypeFromSubserie,
  };
}
